import { Injectable } from '@angular/core';
import { Enrollment } from '../entities/enrollment';
import { CourseStatus } from '../enums/course-status';
import { environment } from 'src/environments/environment';
import * as moment from 'moment-timezone';
import { UntypedFormGroup } from '@angular/forms';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  timezone = 'America/New_York';
  patterns = {
    phone: /^(1\s?)?((\([0-9]{3}\))|[0-9]{3})[\s\-]?[\0-9]{3}[\s\-]?[0-9]{4}$/,
    zip: '^[0-9]{5}(?:-[0-9]{4})?$',
    money: /(\d)(?=(\d{3})+(?!\d))/,
  };
  masks = {
    date: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
    phone: ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
    zip: [/\d/, /\d/, /\d/, /\d/, /\d/],
  };

  constructor() {}

  /**
   * This is to update the enrollment with readable times for amount of time until they can start the course and amount of time left until it expires.
   * @param enrollment The enrollment
   */
  updateEnrollmentTimes(enrollment: Enrollment) {
    let serverTime = moment()
      .add((environment.settings as any).TimeOffset)
      .tz(this.timezone);

    let startDate = moment.utc(enrollment.EnrollStartDate).tz(this.timezone, true).startOf('day');
    enrollment.TimeUntilStart = startDate.diff(serverTime);
    enrollment.TimeUntilStartReadable = moment.duration(enrollment.TimeUntilStart).humanize();

    let endDate = moment.utc(enrollment.EnrollEndDate).tz(this.timezone, true).endOf('day');
    enrollment.TimeLeft = endDate.diff(serverTime);
    enrollment.TimeLeftReadable = moment.duration(enrollment.TimeLeft).humanize();

    // If there is no time left set it to expired because server may not have had chance to update it yet (Acp Task hasn't run yet)
    if (enrollment.Status === CourseStatus.Active && enrollment.TimeLeft < 0) {
      enrollment.Status = CourseStatus.Expired;
    }
  }

  /**
   * This is used to take an array of generic objects (usually returned from the server) and convert it
   * to an array of entities representing that object.  It also converts date strings into date objects
   * @param objects Array of objects
   * @param type The type of object to convert to
   */
  toArrayOfEntity(objects: any[], type) {
    const ret = [];
    if (!objects) {
      return ret;
    }
    for (const obj of objects) {
      // Convert date strings to dates
      for (var k in obj) {
        if (typeof obj[k] === 'string' && obj[k].startsWith('/Date(')) {
          obj[k] = moment(obj[k]).utc(false);
        }
      }
      ret.push(new type(obj));
    }
    return ret;
  }

  // Trim leading and trailing spaces from every field in a form
  trimFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      if (typeof formGroup.get(key).value === 'string') {
        formGroup.get(key).setValue(formGroup.get(key).value.trim());
      }
    });
  }

  /**
   * This will look through the passed array and update the entity if it exists or insert it if it
   * doesn't exist.  It uses the property "Id" to match entities which is the standard identifier.
   * @param array The array to update / insert into
   * @param obj The object to update / insert
   */
  updateEntityInArray(array: any[], obj: any) {
    const existing = array.find((i) => i.Id === obj.Id);
    if (existing) {
      // Update properties
      for (var k in obj) existing[k] = obj[k];
    } else {
      // Insert new object
      array.push(obj);
    }
  }

  renderYesNo(params) {
    return params.value ? 'Yes' : 'No';
  }

  renderGender(params) {
    switch (params.value) {
      case 'Male':
        return 'M';
      case 'Female':
        return 'F';
      default:
        return '';
    }
  }

  phoneFormatter(params) {
    return params.value ? params.value.replace(/^(\d{3})(\d{3})(\d{4}).*/, '($1) $2-$3') : '';
  }

  renderDate(params) {
    return params.value ? moment(params.value).format('MM/DD/YYYY') : '';
  }

  renderDateTime(params) {
    return params.value ? moment(params.value).utc(false).format('MM/DD/YYYY h:mm A') : '';
  }

  /**
   * Util to get current date time using time zone information.
   */
  getCurrentDateTime() {
    return moment().tz(this.timezone);
  }

  /**
   * Util to convert UTC time to a given time zone
   *
   * variable tz = time zone abbreviation
   */
  convertUtcToLocal(datetime, tz) {
    var tzName: string;
    switch (tz) {
      case 'EST':
      case 'EDT':
        tzName = 'America/New_York';
        break;
      case 'CST':
      case 'CDT':
        tzName = 'America/Chicago';
        break;
      case 'MST':
      case 'MDT':
        tzName = 'America/Denver';
        break;
      case 'PST':
      case 'PDT':
        tzName = 'America/Los_Angeles';
        break;
      case 'AKST':
      case 'AKDT':
        tzName = 'America/Anchorage';
        break;
      case 'HST':
      case 'HDT':
        tzName = 'Pacific/Honolulu';
        break;
      default:
        tzName = 'America/New_York';
        break;
    }
    const convertedDate = moment.utc(datetime).tz(tzName);
    return convertedDate;
  }

  /**
   * Converts timezone abbreviation to timezone name
   *
   * variable tz = timezone abbreviation
   */
  convertToTzName(tz) {
    var tzName: string;
    switch (tz) {
      case 'EST':
        tzName = 'Eastern Standard Time';
        break;
      case 'EDT':
        tzName = 'Eastern Daylight Time';
        break;
      case 'CST':
        tzName = 'Central Standard Time';
        break;
      case 'CDT':
        tzName = 'Central Daylight Time';
        break;
      case 'MST':
        tzName = 'Mountain Standard Time';
        break;
      case 'MDT':
        tzName = 'Mountain Daylight Time';
        break;
      case 'PST':
        tzName = 'Pacific Standard Time';
        break;
      case 'PDT':
        tzName = 'Pacific Daylight Time';
        break;
      case 'AKST':
        tzName = 'Alaska Standard Time';
        break;
      case 'AKDT':
        tzName = 'Alaska Daylight Time';
        break;
      case 'HST':
        tzName = 'Hawaii–Aleutian Standard Time';
        break;
      case 'HDT':
        tzName = 'Hawaii–Aleutian Daylight Time';
        break;
      default:
        tzName = 'Eastern Standard Time';
        break;
    }
    return tzName;
  }

  isMobileDevice(): boolean {
    if (Capacitor.isNativePlatform()) {
      return true;
    }
    return false;
  }

  getRootDomainUrl() {
    if (environment.env === 'dev' || Capacitor.isNativePlatform()) return environment.rootDomainUrl;
    return window.location.protocol + '//' + this.insertIntoTopMostSubdomain('api');
  }

  insertIntoTopMostSubdomain(value) {
    var parts = window.location.hostname.split('.');
    if (parts.length < 3) {
      // For hosts without a subdomain or with only a single subdomain (like 'example.com')
      return value + '.' + window.location.hostname;
    } else {
      // Insert the value into the second position of the parts array
      parts.splice(1, 0, value);
      return parts.join('.');
    }
  }

  calculateAge(DateOfBirth: Date): number {
    return moment().diff(moment(DateOfBirth), 'years', true);
  }
}
